import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Plug, Save, Wifi, Send, Copy, CheckCircle2, XCircle, Pause, Play } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function ErpSettingsPage({ embedded = false }) {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [baseUrl, setBaseUrl] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [allowedIps, setAllowedIps] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [backfilling, setBackfilling] = useState(false);
  const [backfillStatus, setBackfillStatus] = useState(null);

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  useEffect(() => { load(); loadBackfillStatus(); }, []);

  // Poll solange der Backfill läuft, damit das Ergebnis automatisch erscheint
  useEffect(() => {
    if (backfillStatus?.status !== 'running') return;
    const t = setInterval(loadBackfillStatus, 2500);
    return () => clearInterval(t);
  }, [backfillStatus?.status]);

  const loadBackfillStatus = async () => {
    try {
      const { data } = await axios.get(`${API}/erp/backfill-status`, auth());
      setBackfillStatus(data);
    } catch (e) { /* still, optionales Feedback */ }
  };

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/erp/config`, auth());
      setCfg(data);
      setBaseUrl(data.base_url || '');
      setAllowedIps((data.allowed_ips || []).join('\n'));
    } catch (e) {
      toast.error('Fehler beim Laden der ERP-Konfiguration');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = { base_url: baseUrl, allowed_ips: allowedIps };
      if (apiKey.trim()) body.api_key = apiKey.trim();
      if (secret.trim()) body.webhook_secret = secret.trim();
      await axios.put(`${API}/erp/config`, body, auth());
      toast.success('ERP-Konfiguration gespeichert');
      setApiKey(''); setSecret('');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post(`${API}/erp/test-connection`, {}, auth());
      setTestResult(data);
      if (data.ok) toast.success(data.detail || 'Verbindung erfolgreich');
      else toast.error(data.detail || 'Verbindung fehlgeschlagen');
    } catch (e) {
      setTestResult({ ok: false, detail: e.response?.data?.detail || 'Test fehlgeschlagen' });
      toast.error(e.response?.data?.detail || 'Test fehlgeschlagen');
    } finally {
      setTesting(false);
    }
  };

  const toggleOutbound = async (enabled) => {
    try {
      await axios.put(`${API}/erp/config`, { outbound_enabled: enabled }, auth());
      toast.success(enabled ? 'Rechnungs-Versand ans ERP aktiviert' : 'Rechnungs-Versand ans ERP gestoppt');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Umschalten fehlgeschlagen');
    }
  };

  const runBackfill = async () => {
    if (!window.confirm('Alle Rechnungen (offen UND bezahlt) jetzt nachträglich ans ERP senden? Offene Rechnungen werden angelegt, bezahlte zusätzlich als bezahlt gemeldet. Mehrfachsenden ist sicher (idempotent).')) return;
    setBackfilling(true);
    try {
      const { data } = await axios.post(`${API}/erp/backfill-all`, {}, auth());
      toast.success(data.message || 'Backfill gestartet');
      // Status sofort auf "running" setzen → Polling startet
      setBackfillStatus({ status: 'running', started_at: new Date().toISOString() });
      setTimeout(loadBackfillStatus, 1500);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Backfill fehlgeschlagen');
    } finally {
      setBackfilling(false);
    }
  };

  const webhookUrl = cfg ? `${BACKEND}${cfg.webhook_path}` : '';
  const copyWebhook = async () => {
    try { await navigator.clipboard.writeText(webhookUrl); toast.success('Webhook-URL kopiert'); } catch {}
  };

  if (loading) return <div className="p-8 text-gray-500">Lädt...</div>;

  return (
    <div className="p-8 max-w-3xl">
      {!embedded && (
        <Button variant="ghost" size="sm" className="mb-4" onClick={() => navigate('/settings')} data-testid="erp-back-btn">
          <ArrowLeft className="h-4 w-4 mr-1" /> Zurück zu Einstellungen
        </Button>
      )}

      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Plug className="h-6 w-6 text-cyan-600" /> Buchhaltung / ERP
          </h1>
          <p className="text-gray-600 mt-1">Zugangsdaten der Buchhaltungssoftware für den automatischen Rechnungs-Sync.</p>
        </div>
        {cfg?.configured
          ? <Badge className="bg-green-100 text-green-800 flex items-center gap-1" data-testid="erp-status-badge"><CheckCircle2 className="h-4 w-4" /> Verbunden</Badge>
          : <Badge className="bg-gray-100 text-gray-600 flex items-center gap-1" data-testid="erp-status-badge"><XCircle className="h-4 w-4" /> Nicht konfiguriert</Badge>}
      </div>

      <Card className={`mb-6 border-2 ${cfg?.outbound_enabled ? 'border-green-200' : 'border-amber-300 bg-amber-50/40'}`} data-testid="erp-outbound-card">
        <CardContent className="py-4 flex items-center justify-between gap-4">
          <div className="flex items-start gap-3">
            {cfg?.outbound_enabled
              ? <Play className="h-5 w-5 text-green-600 mt-0.5 shrink-0" />
              : <Pause className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />}
            <div>
              <div className="font-medium text-gray-900">Rechnungs-Versand ans ERP</div>
              <p className="text-sm text-gray-600 mt-0.5">
                {cfg?.outbound_enabled
                  ? 'Aktiv — neue und bezahlte Rechnungen werden automatisch ans ERP gesendet.'
                  : 'Gestoppt — es werden KEINE Rechnungen, Zahlungen oder sonstige Daten ans ERP gesendet. Eingehende ERP-Webhooks bleiben unberührt.'}
              </p>
            </div>
          </div>
          <Switch
            checked={!!cfg?.outbound_enabled}
            onCheckedChange={toggleOutbound}
            data-testid="erp-outbound-toggle"
          />
        </CardContent>
      </Card>

      <Card className="mb-6">
        <CardHeader>
          <CardTitle className="text-base">Zugangsdaten</CardTitle>
          <CardDescription>Vom ERP-Anbieter erhalten (Einstellungen → Integrationen → StrongFit-App).</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Base-URL</label>
            <Input value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
              placeholder="https://buchhaltung.example.com" data-testid="erp-base-url-input" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">API-Key</label>
            <Input type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
              placeholder={cfg?.api_key_set ? `Gesetzt: ${cfg.api_key_masked} — neu eingeben zum Ändern` : 'sf_…'}
              data-testid="erp-api-key-input" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Webhook-Secret</label>
            <Input type="password" value={secret} onChange={(e) => setSecret(e.target.value)}
              placeholder={cfg?.webhook_secret_set ? `Gesetzt: ${cfg.webhook_secret_masked} — neu eingeben zum Ändern` : 'whsec_…'}
              data-testid="erp-secret-input" className="mt-1" />
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Erlaubte IP-Adressen (optional)</label>
            <textarea value={allowedIps} onChange={(e) => setAllowedIps(e.target.value)}
              placeholder={'Leer = aus (keine IP-Beschränkung).\nEine IP oder CIDR pro Zeile, z. B.:\n203.0.113.10\n203.0.113.0/24'}
              data-testid="erp-allowed-ips-input"
              className="mt-1 w-full rounded-md border border-gray-200 px-3 py-2 text-sm font-mono h-24" />
            <p className="mt-1 text-xs text-gray-500">Defense-in-Depth: Wenn gesetzt, werden Webhooks nur von diesen Server-IPs akzeptiert (die IPs bekommst du von ch-buchhaltung.ch). Leer lassen = kein IP-Filter.</p>
          </div>
          <div className="flex flex-wrap gap-2 pt-2">
            <Button onClick={save} disabled={saving} data-testid="erp-save-btn">
              <Save className="h-4 w-4 mr-1" /> {saving ? 'Speichern…' : 'Speichern'}
            </Button>
            <Button variant="outline" onClick={testConnection} disabled={testing} data-testid="erp-test-btn">
              <Wifi className="h-4 w-4 mr-1" /> {testing ? 'Teste…' : 'Verbindung testen'}
            </Button>
            <Button variant="outline" onClick={runBackfill} disabled={backfilling || backfillStatus?.status === 'running'} data-testid="erp-backfill-btn">
              <Send className="h-4 w-4 mr-1" /> {(backfilling || backfillStatus?.status === 'running') ? 'Läuft…' : 'Alle Rechnungen nachsenden'}
            </Button>
          </div>

          {backfillStatus?.status && (
            <div
              className={`mt-1 rounded-md border px-3 py-2 text-sm ${
                backfillStatus.status === 'running'
                  ? 'bg-blue-50 border-blue-200 text-blue-800'
                  : backfillStatus.status === 'error'
                  ? 'bg-red-50 border-red-200 text-red-700'
                  : 'bg-green-50 border-green-200 text-green-800'
              }`}
              data-testid="erp-backfill-status"
            >
              {backfillStatus.status === 'running' ? (
                <div className="flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                  <span>Nachsende-Lauf läuft… Rechnungen werden ans ERP übertragen.</span>
                </div>
              ) : backfillStatus.status === 'error' ? (
                <div className="flex items-start gap-2">
                  <XCircle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>Lauf fehlgeschlagen: {backfillStatus.error || 'Unbekannter Fehler'}. Bereits gesendet: {backfillStatus.sent || 0}.</span>
                </div>
              ) : (
                <div className="flex items-start gap-2">
                  <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    Letzter Nachsende-Lauf: <strong data-testid="erp-backfill-sent">{backfillStatus.sent || 0}</strong> gesendet
                    {' '}(davon <strong>{backfillStatus.paid_marked || 0}</strong> als bezahlt gemeldet),
                    {' '}<strong>{backfillStatus.failed || 0}</strong> fehlgeschlagen, {backfillStatus.skipped || 0} übersprungen
                    {backfillStatus.finished_at && (
                      <> · {new Date(backfillStatus.finished_at).toLocaleString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</>
                    )}
                  </span>
                </div>
              )}
            </div>
          )}
          {testResult && (
            <div
              className={`mt-1 flex items-start gap-2 rounded-md border px-3 py-2 text-sm ${
                testResult.ok
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-red-50 border-red-200 text-red-700'
              }`}
              data-testid="erp-test-result"
            >
              {testResult.ok
                ? <CheckCircle2 className="h-4 w-4 mt-0.5 shrink-0" />
                : <XCircle className="h-4 w-4 mt-0.5 shrink-0" />}
              <div>
                {testResult.ok && testResult.company
                  ? <span>Verbunden mit <strong data-testid="erp-company-name">«{testResult.company}»</strong></span>
                  : <span>{testResult.detail}</span>}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Webhook-URL für das ERP</CardTitle>
          <CardDescription>Diese Adresse im ERP hinterlegen (für Bankzahlungs-Rückmeldungen).</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <code className="flex-1 text-sm bg-gray-50 border rounded px-3 py-2 break-all" data-testid="erp-webhook-url">{webhookUrl}</code>
            <Button variant="outline" size="icon" onClick={copyWebhook} data-testid="erp-webhook-copy-btn">
              <Copy className="h-4 w-4" />
            </Button>
          </div>
          <div className="mt-3 rounded-md bg-slate-50 border border-slate-200 p-3 text-xs text-slate-600 space-y-1" data-testid="erp-webhook-security-hint">
            <p className="font-medium text-slate-700">Absicherung</p>
            <p>Die URL enthält deine Mandanten-Kennung (<code>?tenant=…</code>) — jeder Betrieb hat seine eigene, korrekt routende Adresse.</p>
            <p><b>Empfohlen (stärker):</b> Signatur im ERP aktivieren. Header <code>X-ERP-Signature</code> im Format <code>t=&lt;unix&gt;,v1=&lt;hmac&gt;</code>, signiert wird <code>t.&lt;body&gt;</code> per HMAC-SHA256 mit dem Webhook-Secret (Zeitfenster 300 s). Alternativ weiterhin das Secret im Header <code>X-Webhook-Secret</code>.</p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
